import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence, useDragControls, useReducedMotion } from "framer-motion";
import { getSheetBackdropMotion, getSheetPanelMotion, useSheetSlideIn } from "../../utils/sheetMotion";
import axios from "axios";
import { Radar, Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { FaCog, FaFire, FaCoffee, FaSnowflake, FaInfinity, FaSquareFull, FaWaveSquare } from "react-icons/fa";
import { subDays } from "date-fns";
import annotationPlugin from "chartjs-plugin-annotation";
import "chart.js/auto";
import { Chart } from "chart.js";

import api from "../../api/apiHelper";
import { useUserDashboard } from "../../contexts/UserDashboardContext";
import { readApiCache, writeApiCache, fieldDetailsCacheKey } from '../../utils/apiCache';
import Page from "../molecules/Page";
import SubfieldProgress from '../molecules/SubfieldProgress';
import { LEVEL_DEFINITIONS, getThresholdsForField } from "../../utils/gamificationConfig";
import { getBadgeStatus } from "../../utils/dashboardUtils";
import { FiChevronRight, FiZap, FiClock, FiCheck, FiList, FiBarChart2, FiX, FiGitBranch, FiCrosshair, FiTriangle } from 'react-icons/fi';
import { AiOutlineSync } from "react-icons/ai";
import {
  HiBolt, HiSparkles, HiShieldCheck,
  HiAcademicCap, HiTrophy, HiMoon, HiStar,
  HiArrowTrendingUp, HiChartPie,
} from 'react-icons/hi2';

Chart.register(annotationPlugin);

const plDays = (n) => {
    if (n % 10 === 1 && n % 100 !== 11) return 'dzień';
    return 'dni';
};

const useCountUp = (target, duration = 1300) => {
    const [count, setCount] = useState(0);
    const frameRef = useRef(null);
    useEffect(() => {
        if (!target) { setCount(0); return; }
        const start = performance.now();
        const animate = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) frameRef.current = requestAnimationFrame(animate);
            else setCount(target);
        };
        frameRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frameRef.current);
    }, [target, duration]);
    return count;
};

const StreakHeroCard = ({ streak, badgeImage, badgeTextColor, freezes, totalSolved, totalTimeMinutes }) => {
    const animStreak = useCountUp(streak ?? 0, 1000);
    const animSolved = useCountUp(totalSolved, 1400);
    const animMinutes = useCountUp(Math.round(totalTimeMinutes), 1200);
    const animFreezes = useCountUp(freezes ?? 0, 900);

    const hoursDisplay = animMinutes >= 60
        ? `${(animMinutes / 60).toFixed(1)}h`
        : `${animMinutes} min`;

    const stats = [
        { Icon: FiZap,       label: 'Seria',       value: `${animStreak} ${plDays(animStreak)}` },
        { Icon: FaSnowflake, label: 'Zamrożenia',  value: `${animFreezes}` },
        { Icon: FiCheck,     label: 'Rozwiązane',  value: `${animSolved}` },
        { Icon: FiClock,     label: 'Nauka',       value: hoursDisplay },
    ];

    return (
        <div className="w-full mb-8">

            <div className="flex justify-center mb-5">
                <div className="relative">
                    <img src={badgeImage} alt="seria" className="w-28 h-auto drop-shadow-xl" />
                    {(streak ?? 0) > 0 && (
                        <span
                            className="absolute top-1/2 left-1/2 -translate-x-[55%] -translate-y-[50%] font-bold text-[30px] font-apotek leading-none pointer-events-none"
                            style={{ color: badgeTextColor, textShadow: '0px 2px 6px rgba(0,0,0,0.35)' }}
                        >
                            {animStreak}
                        </span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
                {stats.map(({ Icon, label, value }) => (
                    <div key={label} className="bg-white rounded-xl p-3 flex items-center gap-3 border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
                        <div className="w-9 h-9 rounded-lg bg-[#233855] flex items-center justify-center flex-shrink-0">
                            <Icon className="text-[#E09F2E]" size={17} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide leading-none mb-0.5">{label}</p>
                            <p className="text-sm font-bold text-[#233855] font-apotek leading-none tabular-nums">{value}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const metricNames = {
    elo: "Punkty ELO",
    total_problems: "Zadania ogółem",
    total_problems_solved: "Zadania rozwiązane",
    user_solving_time_spent_minutes: "Czas rozwiązywania (minuty)",
};

const colorList = ['#233855', '#007D00', '#8A0000', '#E09F2E', '#345081', '#5a6a7e', '#D46C50', '#4A68D0'];

// Osiągnięcia działowe kluczowane po field_id. Nazwy NIE są unikalne między
// poziomami — "Równania i Nierówności" to dział 8 na podstawie i 43 na rozszerzeniu,
// "Geometria Analityczna" to 12 i 48 — więc dopasowanie po frazie trafiało
// w pierwszy pasujący warunek, czyli w wariant podstawowy.
const FIELD_ACHIEVEMENTS = {
    // ── Matura Podstawowa ──────────────────────────────────────────────
    7: { title: 'Bezwzględna Potęga', icon: HiBolt, color: '#FED7AA', bgColor: '#431407',
        quipAchieved: '|67| zadań. Po prostu 67 zadań.',
        quipPending: '67 zadań z liczb i wyrażeń. Bezwzględna robota.' },
    8: { title: 'Delta Zawsze Dodatnia', icon: FiCheck, color: '#6EE7B7', bgColor: '#052E16',
        quipAchieved: 'Delta dodatnia, dwa pierwiastki. Marzenie zostało spełnione.',
        quipPending: '67 zadań z równań. Delta zawsze dodatnia.' },
    10: { title: 'Ciąg Fibonacciego', icon: HiStar, color: '#FDE68A', bgColor: '#451A03',
        quipAchieved: '1, 1, 2, 3, 5, 8... Po prostu ciąg Fibonacciego.',
        quipPending: '67 zadań z ciągów. 1, 1, 2, 3, 5, 8...' },
    11: { title: 'Uśmiechnięta Parabola', icon: HiSparkles, color: '#FDE68A', bgColor: '#451A03',
        quipAchieved: 'Współczynnik a jest dodatni. Parabola się uśmiecha.',
        quipPending: '67 zadań z funkcji i parabola w końcu się uśmiechnie.' },
    12: { title: 'Uczciwy Układ', icon: HiShieldCheck, color: '#6EE7B7', bgColor: '#052E16',
        quipAchieved: 'Układ współrzędnych nigdy nie kłamie. Ty też nie - 67 zadań za Tobą.',
        quipPending: '67 zadań z geometrii analitycznej. Uczciwy układ wymaga uczciwej roboty.' },
    13: { title: 'Sinusoidalny Spokój', icon: HiMoon, color: '#BAE6FD', bgColor: '#0C1A3A',
        quipAchieved: 'Geometria płaska nie ma przed Tobą tajemnic. Sinusoidalny spokój osiągnięty.',
        quipPending: '67 zadań z geometrii płaskiej. Pitagoras czeka z kawą.' },
    14: { title: 'Maturalny Iceberg', icon: FaSnowflake, color: '#BAE6FD', bgColor: '#0C1A3A',
        quipAchieved: 'Im głębiej w stereometrię, tym zimniej. To tylko wierzchołek góry lodowej.',
        quipPending: '67 zadań ze stereometrii. Iceberg widoczny tylko dla wytrwałych.' },
    15: { title: 'Prawdopodobnie Zdasz', icon: HiChartPie, color: '#D8B4FE', bgColor: '#3B0764',
        quipAchieved: 'Prawdopodobieństwo zdania matury właśnie wzrosło. Statystycznie rzecz biorąc.',
        quipPending: '67 zadań ze statystyki. Prawdopodobnie warto.' },

    // ── Matura Rozszerzona ─────────────────────────────────────────────
    41: { title: 'Wykładniczy Wzrost', icon: HiArrowTrendingUp, color: '#FED7AA', bgColor: '#431407',
        quipAchieved: 'Twoja wiedza rośnie wykładniczo!',
        quipPending: 'Rozwiąż 67 zadań, żeby odpalić wzrost wykładniczy.' },
    42: { title: 'Wysokofunkcjonujący Matematyk', icon: HiSparkles, color: '#FDE68A', bgColor: '#451A03',
        quipAchieved: 'Inni ludzie czasem nie rozumieją, że po prostu masz swoją ulubioną funkcję.',
        quipPending: 'Zrób 67 zadań i poznaj swoją ulubioną funkcję.' },
    43: { title: 'Bezwzględny', icon: HiBolt, color: '#6EE7B7', bgColor: '#052E16',
        quipAchieved: 'Bezwzględnie rozprawiasz się z zadaniami.',
        quipPending: 'Zrób 67 zadań i nie przejmuj się maturą.' },
    44: { title: 'Twarde Warunki', icon: HiShieldCheck, color: '#BAE6FD', bgColor: '#0C1A3A',
        quipAchieved: 'To Ty dyktujesz warunki na pierwiastki.',
        quipPending: 'Nie pozwól CKE dyktować warunków na pierwiastki.' },
    45: { title: 'Cosinusoidalny Spokój', icon: FaWaveSquare, color: '#C4B5FD', bgColor: '#1E1B4B',
        quipAchieved: 'Jesteś w 4 ćwiartce cosinusa.',
        quipPending: 'Rozwiąż 67 zadań, żeby przejść do 4 ćwiartki.' },
    46: { title: 'Poznaj Swoje Granice', icon: HiTrophy, color: '#FDE68A', bgColor: '#451A03',
        quipAchieved: 'Te achievementy z 67 przechodzą wszelkie granice.',
        quipPending: '67 zadań i kolejna granica przekroczona.' },
    47: { title: 'Average Drzewko Enjoyer', icon: FiGitBranch, color: '#D8B4FE', bgColor: '#3B0764',
        quipAchieved: 'Udało Ci się narysować więcej drzewek niż na plastyce.',
        quipPending: 'Zrób 67 zadań, aby ogarnąć, o co chodzi z tym drzewkiem.' },
    48: { title: 'Współrzędne Celu', icon: FiCrosshair, color: '#A7F3D0', bgColor: '#022C22',
        quipAchieved: 'Podaję współrzędne: 52°15\'04.7"N 20°59\'38.0"E',
        quipPending: 'Zrób 67 zadań i zdobądź współrzędne celu.' },
    49: { title: 'Trójkąt z Powietrza', icon: FiTriangle, color: '#BAE6FD', bgColor: '#0C1A3A',
        quipAchieved: 'Odblokowano twierdzenie o trójkącie z powietrza.',
        quipPending: 'Zrób 67 zadań i odblokuj twierdzenie o trójkącie z powietrza.' },
    // Oba teksty celowo identyczne — żart polega na tym, że nagroda nie zmienia treści.
    50: { title: 'Oloid', icon: FaInfinity, color: '#BAE6FD', bgColor: '#0C1A3A',
        quipAchieved: 'Oloid to bryła rozwijalna utworzona z 2 prostopadłych okręgów. Toczy się po płaszczyźnie każdym punktem swojej powierzchni.',
        quipPending: 'Oloid to bryła rozwijalna utworzona z 2 prostopadłych okręgów. Toczy się po płaszczyźnie każdym punktem swojej powierzchni.' },
    51: { title: 'Optymalizuj Grind', icon: FaCoffee, color: '#FED7AA', bgColor: '#431407',
        quipAchieved: 'Co Cię nie zabije, to Cię zoptymalizuje.',
        quipPending: 'Musisz trochę przygrindować te zadanka.' },
    52: { title: 'QED i ELO', icon: FaSquareFull, color: '#6EE7B7', bgColor: '#052E16',
        quipAchieved: 'Koniec dowodu i dziękuję.',
        quipPending: 'Wykaż nierówność trójkąta na 67 sposobów.' },
};

// Fallback po nazwie — dla działów spoza matury (tor "Student": granice, pochodne,
// całki, szeregi). Tam id nie są przypisane na sztywno, a nazwy się nie dublują.
const getFieldAchievementByName = (fieldName) => {
    const n = (fieldName || '').toLowerCase();
    if (n.includes('funkcj'))
        return { title: 'Uśmiechnięta Parabola', icon: HiSparkles, color: '#FDE68A', bgColor: '#451A03',
            quipAchieved: 'Współczynnik a jest dodatni. Parabola się uśmiecha.',
            quipPending: '67 zadań z funkcji i parabola w końcu się uśmiechnie.' };
    if (n.includes('planimetr') || n.includes('geometria płask') || n.includes('płaszczyzn'))
        return { title: 'Sinusoidalny Spokój', icon: HiMoon, color: '#BAE6FD', bgColor: '#0C1A3A',
            quipAchieved: 'Geometria płaska nie ma przed Tobą tajemnic. Sinusoidalny spokój osiągnięty.',
            quipPending: '67 zadań z geometrii płaskiej. Pitagoras czeka z kawą.' };
    if (n.includes('statystyk') || n.includes('prawdopodobn') || n.includes('kombinat'))
        return { title: 'Prawdopodobnie Zdasz', icon: HiBolt, color: '#D8B4FE', bgColor: '#3B0764',
            quipAchieved: 'Prawdopodobieństwo zdania matury właśnie wzrosło. Statystycznie rzecz biorąc.',
            quipPending: '67 zadań ze statystyki. Prawdopodobnie warto.' };
    if (n.includes('geometria analityczna') || n.includes('analityczn'))
        return { title: 'Uczciwy Układ', icon: HiShieldCheck, color: '#6EE7B7', bgColor: '#052E16',
            quipAchieved: 'Układ współrzędnych nigdy nie kłamie. Ty też nie - 67 zadań za Tobą.',
            quipPending: '67 zadań z geometrii analitycznej. Uczciwy układ wymaga uczciwej roboty.' };
    if (n.includes('stereometr') || n.includes('przestrzen') || n.includes('przestrzenn'))
        return { title: 'Maturalny Iceberg', icon: FaSnowflake, color: '#BAE6FD', bgColor: '#0C1A3A',
            quipAchieved: 'Im głębiej w stereometrię, tym zimniej. To tylko wierzchoek gry lodowej.',
            quipPending: '67 zadań ze stereometrii. Iceberg widoczny tylko dla wytrwałych.' };
    if (n.includes('ciąg'))
        return { title: 'Ciąg Fibonacciego', icon: HiStar, color: '#FDE68A', bgColor: '#451A03',
            quipAchieved: '1, 1, 2, 3, 5, 8... Po prostu ciąg Fibonacciego.',
            quipPending: '67 zadań z ciągów. 1, 1, 2, 3, 5, 8...' };
    if (n.includes('równan') || n.includes('nierówn'))
        return { title: 'Delta Zawsze Dodatnia', icon: FiCheck, color: '#6EE7B7', bgColor: '#052E16',
            quipAchieved: 'Delta dodatnia, dwa pierwiastki. Marzenie zostało spełnione.',
            quipPending: '67 zadań z równań. Delta zawsze dodatnia.' };
    if (n.includes('liczby') || n.includes('wyrażen') || n.includes('rzeczywist'))
        return { title: 'Bezwzględna Potęga', icon: HiBolt, color: '#FED7AA', bgColor: '#431407',
            quipAchieved: '|67| zadań. Po prostu 67 zadań.',
            quipPending: '67 zadań z liczb i wyrażeń. Bezwzględna robota.' };
    if (n.includes('trygonometr'))
        return { title: 'Sinusik', icon: HiSparkles, color: '#C4B5FD', bgColor: '#1E1B4B',
            quipAchieved: 'sin²(x) + cos²(x) = 1. I 67 zadań = ogarniasz trygonometrię na poziomie.',
            quipPending: 'Rozwiąż 67 zadań z trygonometrii. Każdy kąt ma swoje prawa.' };
    if (n.includes('logarytm') || n.includes('potęg') || n.includes('wykładnicz'))
        return { title: 'Logaryzmik', icon: FaFire, color: '#FED7AA', bgColor: '#431407',
            quipAchieved: 'log(100 zadań) = 2. Ale Twój sukces jest wykładniczy.',
            quipPending: '67 zadań z logarytmów. Każda dekada to krok naprzód.' };
    if (n.includes('pochodn') || n.includes('całk'))
        return { title: 'Pochodny Geniusz', icon: HiSparkles, color: '#BAE6FD', bgColor: '#082F49',
            quipAchieved: 'Pochodna Twojego postępu jest zdecydowanie dodatnia.',
            quipPending: '67 zadań z pochodnych. Granica Twoich możliwości jest nieoznaczona.' };
    if (n.includes('granic'))
        return { title: 'Mistrz Granic', icon: HiTrophy, color: '#FDE68A', bgColor: '#451A03',
            quipAchieved: 'Granice poznałeś od środka i od zewnątrz.',
            quipPending: '67 zadań z granic ciągów. Twoje granice są do przekraczania.' };
    if (n.includes('macierz') || n.includes('układ'))
        return { title: 'Systemowiec', icon: HiShieldCheck, color: '#6EE7B7', bgColor: '#052E16',
            quipAchieved: 'Układy równań układasz jak IKEA instrukcje. Bez patrzenia.',
            quipPending: '67 zadań z układów. System działa.' };
    return { title: '67!', icon: HiStar, color: '#A7F3D0', bgColor: '#022C22',
        quipAchieved: '67 zadań z tego działu. Setka bez pudła.',
        quipPending: 'Rozwiąż 67 zadań z tego działu i zdobądź Setkę!' };
};

const getFieldAchievement = (field) =>
    FIELD_ACHIEVEMENTS[field?.field_id] || getFieldAchievementByName(field?.field_name);

// ============================================================
// ACHIEVEMENT COMPONENTS
// ============================================================


const getFieldRank = (fieldId, elo, solved) => {
    const thresholds = getThresholdsForField(fieldId);
    return [...LEVEL_DEFINITIONS].reverse().find(l => {
        const req = thresholds[l.name];
        return elo >= req.elo && solved >= req.tasks;
    }) || null;
};

const RankProgressBar = ({ label, current, target, met }) => {
    const pct = target > 0 ? Math.min(97, Math.round((current / target) * 100)) : 0;
    return (
        <div className="w-full">
            <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1">
                <span>{label}</span>
                <span className={met ? 'text-[#007D00]' : ''}>{current} / {target}+</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                <div
                    className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${pct}%`, backgroundColor: met ? '#007D00' : '#9CA3AF' }}
                />
            </div>
        </div>
    );
};

const FieldRankCard = ({ field, stats, isSelected, onClick }) => {
    const elo = stats?.elo || 0;
    const solved = stats?.total_problems_solved || 0;
    const rank = getFieldRank(field.field_id, elo, solved);
    const thresholds = getThresholdsForField(field.field_id);

    const currentRankIndex = rank ? LEVEL_DEFINITIONS.findIndex(l => l.name === rank.name) : -1;
    const nextRankDef = currentRankIndex >= 0 && currentRankIndex < LEVEL_DEFINITIONS.length - 1
        ? LEVEL_DEFINITIONS[currentRankIndex + 1] : null;
    const nextReq = nextRankDef ? thresholds[nextRankDef.name] : null;
    const firstReq = thresholds[LEVEL_DEFINITIONS[0].name];
    const targetReq = nextReq || firstReq;

    return (
        <motion.button
            onClick={onClick}
            whileTap={{ scale: 0.94 }}
            className={`w-full relative overflow-hidden text-left rounded-xl border transition-colors duration-200 ${
                isSelected
                    ? `p-4 ${rank ? 'bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)]' : 'bg-gray-50 border-gray-200'}`
                    : rank
                        ? 'p-2.5 bg-white hover:shadow-sm'
                        : 'p-2.5 bg-gray-50/50 border-gray-100'
            }`}
            style={rank ? { borderColor: '#E09F2E' } : undefined}
        >
            {isSelected ? (
                <div className="flex flex-col items-center text-center gap-3">
                    <img
                        src={rank ? rank.icon.achieved : LEVEL_DEFINITIONS[0].icon.notAchieved}
                        alt={rank ? rank.name : 'Brak rangi'}
                        className={`w-16 h-16 ${!rank ? 'opacity-20 grayscale' : ''}`}
                    />
                    <div className="w-full">
                        <p className="text-sm font-bold text-[#233855] leading-tight mb-0.5">{field.field_name}</p>
                        <p className={`text-xs font-semibold mb-1 ${rank ? 'text-[#E09F2E]' : 'text-gray-400'}`}>
                            {rank ? rank.name : 'Brak rangi'}
                        </p>
                        {rank && (
                            <p className="text-sm text-gray-500 leading-relaxed mb-3">{rank.description}</p>
                        )}
                        {rank && !nextRankDef ? (
                            <span className="text-xs font-semibold text-[#007D00] bg-[#007D00]/10 px-2 py-1 rounded-full">
                                Maksymalna ranga!
                            </span>
                        ) : (
                            <div className="space-y-2.5 text-left">
                                <p className="text-xs font-semibold text-gray-500 text-center">
                                    {rank ? `Do: ${nextRankDef.name}` : `Pierwsza ranga: ${LEVEL_DEFINITIONS[0].name}`}
                                </p>
                                <RankProgressBar label="ELO" current={elo} target={targetReq.elo} met={elo >= targetReq.elo} />
                                <RankProgressBar label="Zadania" current={solved} target={targetReq.tasks} met={solved >= targetReq.tasks} />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center text-center">
                    <img
                        src={rank ? rank.icon.achieved : LEVEL_DEFINITIONS[0].icon.notAchieved}
                        alt={rank ? rank.name : 'Brak rangi'}
                        className={`w-10 h-10 mb-1.5 ${!rank ? 'opacity-20 grayscale' : ''}`}
                    />
                    <span className={`text-sm font-semibold text-center leading-tight line-clamp-2 w-full ${rank ? 'text-gray-700' : 'text-gray-300'}`}>
                        {field.field_name}
                    </span>
                    <span className={`text-xs font-medium mt-0.5 ${rank ? 'text-[#E09F2E]' : 'text-gray-200'}`}>
                        {rank ? rank.name : '-'}
                    </span>
                </div>
            )}
        </motion.button>
    );
};

const AchievementTile = ({ label, sublabel, icon: Icon, achieved, color, bgColor, isSelected, onClick, quip, progressCurrent, progressTotal, isTimed = false, forceMinutes = false }) => {
    const pct = progressTotal > 0 ? Math.min(97, Math.round((progressCurrent / progressTotal) * 100)) : 0;
    const progressLabel = isTimed
        ? forceMinutes
            ? `${progressCurrent} / ${progressTotal} min`
            : `${(progressCurrent / 60).toFixed(1)} / ${Math.round(progressTotal / 60)} godz.`
        : `${progressCurrent} / ${progressTotal} zadań`;

    return (
        <motion.button
            onClick={onClick}
            whileTap={{ scale: 0.94 }}
            className={`w-full relative overflow-hidden text-left rounded-xl border transition-colors duration-200 ${
                isSelected
                    ? `p-4 ${achieved ? 'bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)]' : 'bg-gray-50 border-gray-200'}`
                    : achieved
                        ? 'p-3 bg-white hover:shadow-sm'
                        : 'p-3 bg-gray-50/50 border-gray-100'
            }`}
            style={achieved ? { borderColor: bgColor } : undefined}
        >
            {isSelected ? (
                <div className="flex flex-col items-center text-center gap-3">
                    <div
                        className="rounded-2xl flex items-center justify-center relative overflow-hidden"
                        style={{
                            width: 64, height: 64,
                            backgroundColor: achieved ? bgColor : '#E5E7EB',
                            boxShadow: achieved ? `0 6px 20px -4px ${color}66` : undefined,
                        }}
                    >
                        <Icon size={30} style={{ color: achieved ? color : '#BDBDBD' }} />
                        {achieved && (
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-sheen pointer-events-none" />
                        )}
                    </div>

                    <div className="w-full">
                        <div className="flex items-center justify-center gap-2 mb-1 flex-wrap">
                            <p className="text-sm font-bold text-[#233855] leading-tight">{label}</p>
                        </div>
                        {sublabel && <p className="text-xs text-gray-400 mb-1.5">{sublabel}</p>}
                        <p className="text-sm text-gray-600 leading-relaxed">{quip}</p>
                    </div>

                    {!achieved && progressTotal > 0 && (
                        <div className="w-full">
                            <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1.5">
                                <span>Postęp</span>
                                <span>{progressLabel}</span>
                            </div>
                            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                                <div
                                    className="h-full rounded-full transition-all duration-700 ease-out"
                                    style={{ width: `${pct}%`, backgroundColor: '#9CA3AF' }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex flex-col items-center text-center">
                    <div
                        className="rounded-xl flex items-center justify-center mb-2 relative overflow-hidden"
                        style={{
                            width: 48, height: 48,
                            backgroundColor: achieved ? bgColor : '#E5E7EB',
                            boxShadow: achieved ? `0 4px 14px -2px ${color}55` : undefined,
                        }}
                    >
                        <Icon size={22} style={{ color: achieved ? color : '#BDBDBD' }} />
                        {achieved && (
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-sheen pointer-events-none" />
                        )}
                    </div>
                    <p className={`text-sm font-semibold leading-tight mt-0.5 ${achieved ? 'text-gray-800' : 'text-gray-300'}`}>
                        {label}
                    </p>
                    {sublabel && (
                        <p className={`text-xs mt-0.5 leading-tight ${achieved ? 'text-gray-400' : 'text-gray-200'}`}>
                            {sublabel}
                        </p>
                    )}
                </div>
            )}
        </motion.button>
    );
};

// ============================================================
// SHARED COMPONENTS
// ============================================================

const StatTile = ({ Icon, value, label }) => (
    <div className="flex items-center gap-2.5 bg-white rounded-xl p-2.5 border border-gray-100 shadow-sm">
        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-[#233855]">
            <Icon className="text-[#E09F2E]" size={16} />
        </div>
        <div>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 leading-none">{label}</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5 leading-none">{value}</p>
        </div>
    </div>
);

const RankTimeline = ({ fieldId, elo, solved }) => {
    const thresholds = getThresholdsForField(fieldId);
    const levels = LEVEL_DEFINITIONS.map((levelDef) => {
        const req = thresholds[levelDef.name] || { elo: 0, tasks: 0 };
        const isAchieved = elo >= req.elo && solved >= req.tasks;
        return { ...levelDef, req, isAchieved };
    });

    return (
        <div className="pt-2 pb-1 px-1">
            <div className="flex justify-between items-start">
                {levels.map((level, index) => (
                    <React.Fragment key={level.name}>
                        <div className="flex flex-col items-center w-14 flex-shrink-0">
                            <div className="h-14 flex items-center justify-center">
                                <img
                                    src={level.isAchieved ? level.icon.achieved : level.icon.notAchieved}
                                    alt={level.name}
                                    className={`w-12 h-12 ${!level.isAchieved ? "opacity-40" : ""}`}
                                />
                            </div>
                            <span className={`text-[11px] text-center leading-tight whitespace-nowrap ${level.isAchieved ? "text-[#233855] font-semibold" : "text-gray-400"}`}>
                                {level.name}
                            </span>
                            <span className="text-[11px] text-center text-gray-400 leading-snug">ELO {level.req.elo}+</span>
                            <span className="text-[11px] text-center text-gray-400 leading-snug">{level.req.tasks}+ zad.</span>
                        </div>
                        {index < levels.length - 1 && (
                            <div className={`flex-1 h-px self-start mt-7 ${level.isAchieved ? "bg-[#233855]" : "bg-gray-200"}`} />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

const FieldStatistics = ({ field, stats, onOpen }) => {
    const elo = stats?.elo || 0;
    const solved = stats?.total_problems_solved || 0;

    const thresholds = getThresholdsForField(field.field_id);
    const rank = [...LEVEL_DEFINITIONS].reverse().find(l => {
        const req = thresholds[l.name];
        return req && elo >= req.elo && solved >= req.tasks;
    }) || null;
    const nextRank = LEVEL_DEFINITIONS.find(l => {
        const req = thresholds[l.name];
        return req && !(elo >= req.elo && solved >= req.tasks);
    });
    const nextReq = nextRank ? thresholds[nextRank.name] : null;
    const prevRankIndex = nextRank ? LEVEL_DEFINITIONS.indexOf(nextRank) - 1 : LEVEL_DEFINITIONS.length - 1;
    const prevReq = prevRankIndex >= 0 ? thresholds[LEVEL_DEFINITIONS[prevRankIndex].name] : { elo: 0, tasks: 0 };
    const eloProgress = nextReq
        ? Math.min(100, Math.max(0, Math.round(((elo - prevReq.elo) / (nextReq.elo - prevReq.elo)) * 100)))
        : 100;

    return (
        <button
            type="button"
            onClick={onOpen}
            className="w-full text-left p-3.5 bg-white border border-gray-200 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.07)] hover:border-[#233855]/30 hover:shadow-[0_4px_16px_rgba(35,56,85,0.12)] active:scale-[0.97] active:shadow-sm transition-all duration-150 group flex items-center gap-3"
        >
            <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center">
                {rank ? (
                    <img src={rank.icon.achieved} alt={rank.name} className="w-8 h-8" />
                ) : (
                    <img src={LEVEL_DEFINITIONS[0].icon.notAchieved} alt="Brak rangi" className="w-8 h-8 opacity-25" />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="font-semibold text-sm text-[#233855] leading-snug truncate">{field.field_name}</span>
                    <span className="flex-shrink-0 text-xs text-gray-400 font-medium">ELO {elo}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#233855] rounded-full" style={{ width: `${eloProgress}%` }} />
                    </div>
                    <span className="flex-shrink-0 text-[11px] text-gray-400">
                        {nextRank ? nextRank.name : "Max"}
                    </span>
                </div>
            </div>
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
                <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
            </div>
        </button>
    );
};

const FieldStatsSheet = ({ field, stats, subfields, isLoading, onClose }) => {
    const dragControls = useDragControls();
    const prefersReducedMotion = useReducedMotion();
    const slideIn = useSheetSlideIn(!!field);

    useEffect(() => {
        window.dispatchEvent(new CustomEvent('bottomsheet:open'));
        return () => window.dispatchEvent(new CustomEvent('bottomsheet:close'));
    }, []);
    const elo = stats?.elo || 0;
    const totalProblems = stats?.total_problems || 0;
    const solved = stats?.total_problems_solved || 0;
    const timeSpent = stats?.user_solving_time_spent_minutes ? stats.user_solving_time_spent_minutes.toFixed(1) : '0.0';

    return ReactDOM.createPortal(
        <>
            <motion.div
                key="field-stats-backdrop"
                {...getSheetBackdropMotion()}
                className="fixed inset-0 bg-black/50 z-[9990]"
                onClick={onClose}
            />
            <motion.div
                key="field-stats-sheet"
                {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
                drag="y"
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{ top: 0 }}
                dragElastic={{ top: 0, bottom: 0.4 }}
                onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) onClose(); }}
                className="fixed bottom-0 left-0 right-0 z-[9991] bg-white rounded-t-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[92vh] lg:max-w-2xl lg:mx-auto lg:rounded-2xl"
            >
                <div
                    className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab touch-none"
                    onPointerDown={(e) => dragControls.start(e)}
                >
                    <div className="w-10 h-1 bg-gray-200 rounded-full" />
                </div>
                <div className="flex-shrink-0 px-5 pt-3 pb-0">
                    <div className="flex items-start justify-between gap-3 mb-0.5">
                        <div className="flex-1 min-w-0">
                            <h2 className="font-apotek text-lg text-[#233855] leading-tight">{field.field_name}</h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Twoje ELO: <span className="font-semibold text-[#233855]">{elo}</span>
                                &nbsp;·&nbsp;{solved} rozwiązanych zadań
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                        >
                            <FiX className="w-4 h-4 text-gray-600" />
                        </button>
                    </div>
                    <RankTimeline fieldId={field.field_id} elo={elo} solved={solved} />
                    <div className="grid grid-cols-2 gap-2.5 pt-3 border-t border-gray-100">
                        <StatTile Icon={FiBarChart2} value={elo} label="Punkty ELO" />
                        <StatTile Icon={FiList} value={totalProblems} label="Zadań ogółem" />
                        <StatTile Icon={FiCheck} value={solved} label="Rozwiązane" />
                        <StatTile Icon={FiClock} value={`${timeSpent} min`} label="Czas nauki" />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto px-5 pb-4 pt-3 border-t border-gray-100 space-y-2">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-10 gap-2 text-gray-400 text-sm">
                            <AiOutlineSync className="animate-spin w-4 h-4" />
                            Ładowanie tematów...
                        </div>
                    ) : subfields.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-8">Brak szczegółowych tematów.</p>
                    ) : (
                        subfields.map(sf => <SubfieldProgress key={sf.subfield_id} subfield={sf} />)
                    )}
                </div>
            </motion.div>
        </>,
        document.body
    );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

// Etykiety knowledge_level odpowiadające aktywnej domenie (PP/PR).
const DOMAIN_LABELS = { PP: "Matura Podstawowa", PR: "Matura Rozszerzona" };
const getActiveDomain = () =>
    readApiCache("user_dashboard_data", Infinity)?.account_status?.active_domain || "PP";
// Zostawia tylko pola należące do aktywnej domeny (radar/statystyki są już
// filtrowane po stronie backendu — to zawęża listę rang/działów w UI).
const filterFieldsByDomain = (fields, domain) => {
    const label = DOMAIN_LABELS[domain];
    if (!label || !Array.isArray(fields)) return fields || [];
    return fields.filter((f) => (f.knowledge_level || "") === label);
};

const UserStats = () => {
    const [isRadarChart, setIsRadarChart] = useState(true);
    const [selectedFields, setSelectedFields] = useState([1, 2, 3, 7, 8, 10]);
    const [selectedMetric, setSelectedMetric] = useState("elo");
    const [radarData, setRadarData] = useState(null);
    const [lineData, setLineData] = useState(null);
    const [lineOptions, setLineOptions] = useState(null);
    const [radarOptions, setRadarOptions] = useState(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [showDateRange, setShowDateRange] = useState(false);
    const [fieldMapping, setFieldMapping] = useState(() => {
        const cached = readApiCache('fields_preferences', Infinity);
        if (!Array.isArray(cached?.user_fields)) return {};
        const m = {};
        filterFieldsByDomain(cached.user_fields, getActiveDomain()).forEach(f => { m[f.field_id] = f.field_name; });
        return m;
    });
    const [groupedFields, setGroupedFields] = useState(() => {
        const cached = readApiCache('fields_preferences', Infinity);
        if (!Array.isArray(cached?.user_fields)) return {};
        return filterFieldsByDomain(cached.user_fields, getActiveDomain()).sort((a, b) => a.field_id - b.field_id).reduce((acc, f) => {
            const level = f.knowledge_level || "Inne";
            if (!acc[level]) acc[level] = [];
            acc[level].push(f);
            return acc;
        }, {});
    });
    const [fetchedData, setFetchedData] = useState(() => readApiCache('user_stats', Infinity));
    const [dateRange, setDateRange] = useState("week");
    const [tempChartType, setTempChartType] = useState("radar");
    const [tempSelectedFields, setTempSelectedFields] = useState(selectedFields);
    const [tempSelectedMetric, setTempSelectedMetric] = useState(selectedMetric);
    const [fieldStatsMapping, setFieldStatsMapping] = useState(() => {
        const cached = readApiCache('user_stats', Infinity);
        if (!Array.isArray(cached?.radar_chart?.fields)) return {};
        const m = {};
        cached.radar_chart.fields.forEach(s => { m[s.field_id] = s; });
        return m;
    });
    const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);
    const [selectedRankFieldId, setSelectedRankFieldId] = useState(null);
    const { dashboardData, ensureLoaded } = useUserDashboard();
    const [accountStatus, setAccountStatus] = useState(() => dashboardData?.account_status || null);
    const [statsSheet, setStatsSheet] = useState(null);
    const [statsSubfieldsMap, setStatsSubfieldsMap] = useState({});
    const [statsLoadingFieldId, setStatsLoadingFieldId] = useState(null);
    const loadedStatsFields = useRef(new Set());

    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const panelRef = useRef(null);

    // Self-heal: dociągnij dane kontekstu (np. zaraz po logowaniu)
    useEffect(() => { ensureLoaded(); }, [ensureLoaded]);

    useEffect(() => {
        const savedFields = JSON.parse(localStorage.getItem("selectedFields"));
        const savedMetric = localStorage.getItem("selectedMetric");
        const savedChartType = localStorage.getItem("chartType");
        if (savedFields) { setSelectedFields(savedFields); setTempSelectedFields(savedFields); }
        if (savedMetric) { setSelectedMetric(savedMetric); setTempSelectedMetric(savedMetric); }
        if (savedChartType) { setIsRadarChart(savedChartType === "radar"); setShowDateRange(savedChartType === "line"); setTempChartType(savedChartType); }
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setIsPanelOpen(false); };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const didInitCharts = useRef(false);
    useEffect(() => {
        if (didInitCharts.current) return;
        if (!fetchedData || Object.keys(fieldMapping).length === 0) return;
        didInitCharts.current = true;
        updateRadarChart(fetchedData, fieldMapping);
        updateLineChart(fetchedData, dateRange, fieldMapping);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchedData, fieldMapping]);

    useEffect(() => {
        if (!token) { navigate("/login"); return; }

        const hasFreshStats   = !!readApiCache('user_stats');
        const hasFreshFields  = !!readApiCache('fields_preferences');
        const hasAnyStats     = !!readApiCache('user_stats', Infinity);

        // Świeże dane w cache — renderuj natychmiast, nie fetchuj
        if (hasFreshStats && hasFreshFields) {
            return;
        }

        const fetchInitialData = async () => {
            try {
                const silent = hasAnyStats; // mamy stale dane — fetch w tle
                const [fieldsRes, statsRes] = await Promise.all([
                    api.get('fields_preferences/', { skipLoading: silent }),
                    api.get('user_stats_view/', { skipLoading: silent }),
                ]);
                if (dashboardData?.account_status) setAccountStatus(dashboardData.account_status);
                const activeDomain = dashboardData?.account_status?.active_domain || getActiveDomain();
                const allFields = filterFieldsByDomain(fieldsRes.data.user_fields, activeDomain).sort((a, b) => a.field_id - b.field_id);
                const mapping = {};
                allFields.forEach(f => { mapping[f.field_id] = f.field_name; });
                setFieldMapping(mapping);

                const groups = allFields.reduce((acc, f) => {
                    const level = f.knowledge_level || "Inne";
                    if (!acc[level]) acc[level] = [];
                    acc[level].push(f);
                    return acc;
                }, {});
                setGroupedFields(groups);

                const data = statsRes.data;
                const statsMapping = {};
                if (data?.radar_chart?.fields) {
                    data.radar_chart.fields.forEach(s => { statsMapping[s.field_id] = s; });
                }
                setFieldStatsMapping(statsMapping);
                setFetchedData(data);
                writeApiCache('fields_preferences', fieldsRes.data);
                writeApiCache('user_stats', data);
                updateRadarChart(data, mapping);
                updateLineChart(data, dateRange, mapping);
            } catch (e) {
                console.error("Error fetching stats:", e);
            }
        };
        fetchInitialData();
    }, [token, navigate]);

    const updateRadarChart = (data, mapping) => {
        if (!data?.radar_chart) return;
        const visibleFields = Object.values(groupedFields).flat().filter(f => f.is_visible);
        const radarFields = data.radar_chart.fields.filter(f => selectedFields.includes(f.field_id) && visibleFields.some(vf => vf.field_id === f.field_id));
        const radarLabels = radarFields.map(f => mapping[f.field_id] || `Field ${f.field_id}`);
        const radarMetricData = radarFields.map(f => f[selectedMetric]);

        const splitLabel = (str, maxLen = 15) => {
            if (!str) return [];
            const words = str.split(' ');
            const lines = [];
            let cur = words[0];
            for (let i = 1; i < words.length; i++) {
                if (cur.length + 1 + words[i].length <= maxLen) cur += " " + words[i];
                else { lines.push(cur); cur = words[i]; }
            }
            lines.push(cur);
            return lines;
        };

        setRadarData({
            labels: radarLabels,
            datasets: [{ label: metricNames[selectedMetric], data: radarMetricData, backgroundColor: 'rgba(35, 56, 85, 0.2)', borderColor: '#233855', borderWidth: 2, pointBackgroundColor: '#233855', pointBorderColor: '#fff', pointHoverBackgroundColor: '#fff', pointHoverBorderColor: '#233855' }],
        });
        setRadarOptions({
            layout: { padding: { top: 5, bottom: 5, left: 10, right: 10 } },
            scales: { r: { angleLines: { color: 'rgba(0,0,0,0.1)' }, grid: { color: 'rgba(0,0,0,0.1)' }, ticks: { display: false }, pointLabels: { font: { size: 10 }, color: '#333', callback: (label, index) => { const fd = radarFields[index]; if (!fd) return label; const val = fd[selectedMetric] ?? 'Brak danych'; return [...splitLabel(label), `${metricNames[selectedMetric]}: ${val}`]; } } } },
            plugins: { legend: { display: false }, datalabels: { display: false } },
            maintainAspectRatio: false,
        });
    };

    const updateLineChart = (data, range, mapping) => {
        if (!data?.line_chart) return;
        let rangeDays, interval;
        switch (range) {
            case "week": rangeDays = 7; interval = 1; break;
            case "twoWeeks": rangeDays = 14; interval = 1; break;
            case "month": rangeDays = 30; interval = 2; break;
            case "threeMonths": rangeDays = 90; interval = 7; break;
            case "year": rangeDays = 365; interval = 30; break;
            default: rangeDays = 7; interval = 1;
        }
        const minDate = subDays(new Date(), rangeDays);
        const allDates = [...new Set(data.line_chart.flatMap(f => f.dates))].sort();
        const selectedDates = allDates.filter(d => new Date(d) >= minDate).filter((_, i) => i % interval === 0);
        const visibleFields = Object.values(groupedFields).flat().filter(f => f.is_visible);
        const lineDatasets = data.line_chart
            .filter(f => selectedFields.includes(f.field_id) && visibleFields.some(vf => vf.field_id === f.field_id))
            .map((f, idx) => {
                let lastVal = null;
                const fieldData = selectedDates.map(d => { const di = f.dates.indexOf(d); if (di !== -1) lastVal = f[selectedMetric][di]; return lastVal; });
                return { label: mapping[f.field_id] || `Field ${f.field_id}`, data: fieldData, fill: false, borderColor: colorList[idx % colorList.length], tension: 0.2, borderWidth: 2 };
            });
        const insideRange = new Date(data.user_joined_date) >= new Date(selectedDates[0]) && new Date(data.user_joined_date) <= new Date(selectedDates[selectedDates.length - 1]);
        const annotationOpts = { joinedDateLine: { type: 'line', xMin: data.user_joined_date, xMax: data.user_joined_date, borderColor: 'black', borderWidth: 1, label: { display: true, content: 'Rejestracja', enabled: true, position: 'end', anchor: 'end', xAdjust: -40, color: 'black', backgroundColor: 'rgba(255,255,255,0.8)', font: { size: 12 } } } };
        setLineOptions({ plugins: { datalabels: { display: false }, legend: { labels: { font: { size: 10, family: "'Apotek', sans-serif" }, usePointStyle: true, pointStyle: 'circle', padding: 10, boxWidth: 8, boxHeight: 8 }, position: 'top' }, annotation: { annotations: insideRange ? annotationOpts : {} } }, elements: { point: { radius: 3, hitRadius: 7, backgroundColor: (ctx) => ctx.dataset.borderColor, borderWidth: 0 } }, responsive: true, maintainAspectRatio: false, scales: { x: { grid: { display: false } }, y: { grid: { color: 'rgba(0,0,0,0.05)' } } } });
        setLineData({ labels: selectedDates, datasets: lineDatasets });
    };

    useEffect(() => {
        if (fetchedData) {
            if (isRadarChart) updateRadarChart(fetchedData, fieldMapping);
            else updateLineChart(fetchedData, dateRange, fieldMapping);
        }
    }, [selectedFields, selectedMetric, dateRange, fetchedData, isRadarChart, groupedFields]);

    const handleFieldToggle = (fieldId) => {
        setTempSelectedFields(prev => prev.includes(fieldId) ? prev.filter(id => id !== fieldId) : [...prev, fieldId]);
    };

    const handleRefreshClick = () => {
        setSelectedFields(tempSelectedFields);
        setSelectedMetric(tempSelectedMetric);
        const newIsRadar = tempChartType === "radar";
        setIsRadarChart(newIsRadar);
        setShowDateRange(!newIsRadar);
        localStorage.setItem("selectedFields", JSON.stringify(tempSelectedFields));
        localStorage.setItem("selectedMetric", tempSelectedMetric);
        localStorage.setItem("chartType", tempChartType);
        setIsPanelOpen(false);
    };

    const handleOpenStatsSheet = async (field, stats) => {
        setStatsSheet({ field, stats });
        const fieldId = field.field_id;
        const cacheKey = fieldDetailsCacheKey(fieldId);
        const cached = readApiCache(cacheKey, Infinity);
        const stale = Array.isArray(cached) ? cached : null; // ignoruj stary/zły kształt z cache

        if (stale) {
            setStatsSubfieldsMap(prev => ({ ...prev, [fieldId]: stale }));
        }

        if (loadedStatsFields.current.has(fieldId)) return;

        if (stale) {
            axios.get(
                `${process.env.REACT_APP_API_SOURCE}field-details/${fieldId}/`,
                { headers: { Authorization: `Token ${token}` } }
            ).then(({ data }) => {
                const arr = Array.isArray(data) ? data : [];
                setStatsSubfieldsMap(prev => ({ ...prev, [fieldId]: arr }));
                writeApiCache(cacheKey, arr);
                loadedStatsFields.current.add(fieldId);
            }).catch(() => {});
            return;
        }

        setStatsLoadingFieldId(fieldId);
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_API_SOURCE}field-details/${fieldId}/`,
                { headers: { Authorization: `Token ${token}` } }
            );
            const arr = Array.isArray(data) ? data : [];
            setStatsSubfieldsMap(prev => ({ ...prev, [fieldId]: arr }));
            writeApiCache(cacheKey, arr);
            loadedStatsFields.current.add(fieldId);
        } catch (e) {
            console.error("Błąd pobierania szczegółów działu:", e);
        } finally {
            setStatsLoadingFieldId(null);
        }
    };

    // --- ACHIEVEMENT DATA ---
    const allVisibleFields = useMemo(() => Object.values(groupedFields).flat().filter(f => f.is_visible).sort((a, b) => a.field_id - b.field_id), [groupedFields]);

    const totalSolved = useMemo(() => {
        if (!fetchedData?.radar_chart?.fields) return 0;
        return fetchedData.radar_chart.fields.reduce((sum, f) => sum + (f.total_problems_solved || 0), 0);
    }, [fetchedData]);

    const totalTimeMinutes = useMemo(() => {
        if (!fetchedData?.radar_chart?.fields) return 0;
        return fetchedData.radar_chart.fields.reduce((sum, f) => sum + (f.user_solving_time_spent_minutes || 0), 0);
    }, [fetchedData]);


    const milestones = useMemo(() => [
        // --- ZADANIA ---
        {
            id: 'solve_1', label: 'Zawsze Coś...', sublabel: '1 zadanie',
            icon: HiBolt, color: '#FDE68A', bgColor: '#233855', achieved: totalSolved >= 1,
            progressCurrent: totalSolved, progressTotal: 1,
            quip: totalSolved >= 1
                ? "Twoje pierwsze zadanie."
                : "Zrób dosłownie jedno zadanie. To wszystko. Naprawdę.",
        },
        {
            id: 'solve_67', label: '67 zadań', sublabel: '67 zadań',
            icon: HiSparkles, color: '#D8B4FE', bgColor: '#3B0764', achieved: totalSolved >= 67,
            progressCurrent: totalSolved, progressTotal: 67,
            quip: totalSolved >= 67
                ? "Six seven!"
                : "67 zadań.",
        },
        {
            id: 'solve_100', label: 'Kawa z Pitagorasem', sublabel: '100 zadań',
            icon: FaCoffee, color: '#FEF3C7', bgColor: '#292524', achieved: totalSolved >= 100,
            progressCurrent: totalSolved, progressTotal: 100,
            quip: totalSolved >= 100
                ? "100 zadań. Zasługujesz na kawę z Pitagorasem."
                : "Pitagoras pije kawę tylko z osobami, które mają 100 zadań na koncie.",
        },
        {
            id: 'solve_250', label: 'Przestrzeń Banacha', sublabel: '250 zadań',
            icon: HiAcademicCap, color: '#BAE6FD', bgColor: '#082F49', achieved: totalSolved >= 250,
            progressCurrent: totalSolved, progressTotal: 250,
            quip: totalSolved >= 250
                ? "250 zadań. Jesteś w przestrzeni Banacha - zupełnej i metrycznej."
                : "Przestrzeń Banacha jest zupełna. Twój wynik - jeszcze nie.",
        },
        {
            id: 'solve_500', label: 'Postrach CKE', sublabel: '500 zadań',
            icon: HiTrophy, color: '#FDE68A', bgColor: '#7C0000', achieved: totalSolved >= 500,
            progressCurrent: totalSolved, progressTotal: 500,
            quip: totalSolved >= 500
                ? "500 zadań. Nie bój się CKE. To CKE boi się Ciebie!"
                : "CKE jeszcze nie wie co ją czeka.",
        },
        {
            id: 'solve_1000', label: 'Matma to Życie', sublabel: '1000 zadań',
            icon: HiStar, color: '#A7F3D0', bgColor: '#022C22', achieved: totalSolved >= 1000,
            progressCurrent: totalSolved, progressTotal: 1000,
            quip: totalSolved >= 1000
                ? "1000 zadań. Już nie uczysz się matematyki. Ty żyjesz matematyką."
                : "1000 zadań brzmi dużo.",
        },
        // --- CZAS ---
        {
            id: 'hour_1', label: 'Dojazd do Szkoły', sublabel: '1 godzina nauki',
            icon: FiClock, color: '#BAE6FD', bgColor: '#0C1A3A', achieved: totalTimeMinutes >= 60, isTimed: true,
            progressCurrent: Math.round(totalTimeMinutes), progressTotal: 60,
            quip: totalTimeMinutes >= 60
                ? "Godzina nauki. Tyle co dojazd do szkoły."
                : "Po prostu odpal Matematly w pociągu/busie/tramwaju.",
        },
        {
            id: 'hour_8', label: 'Dniówka', sublabel: '8 godzin nauki',
            icon: FiZap, color: '#FDE68A', bgColor: '#451A03', achieved: totalTimeMinutes >= 480, isTimed: true,
            progressCurrent: Math.round(totalTimeMinutes), progressTotal: 480,
            quip: totalTimeMinutes >= 480
                ? "8 godzin nauki. Pełna matematyczna dniówka."
                : "Pełna  matematyczna dniówka zaliczona.",
        },
        {
            id: 'hour_2137', label: '2137', sublabel: '2137 minut nauki',
            icon: HiMoon, color: '#C4B5FD', bgColor: '#1E1B4B', achieved: totalTimeMinutes >= 2137, isTimed: true,
            progressCurrent: Math.round(totalTimeMinutes), progressTotal: 2137, forceMinutes: true,
            quip: totalTimeMinutes >= 2137
                ? "2137 minut nauki. No tak."
                : "2137 minut nauki.",
        },
        {
            id: 'hour_67', label: '67h', sublabel: '67 godzin nauki',
            icon: HiSparkles, color: '#D8B4FE', bgColor: '#3B0764', achieved: totalTimeMinutes >= 4020, isTimed: true,
            progressCurrent: Math.round(totalTimeMinutes), progressTotal: 4020,
            quip: totalTimeMinutes >= 4020
                ? "Six seven!"
                : "67 godzin nauki.",
        },
        {
            id: 'hour_100', label: 'Chwilka przy Matmie', sublabel: '100 godzin nauki',
            icon: HiTrophy, color: '#FDE68A', bgColor: '#233855', achieved: totalTimeMinutes >= 6000, isTimed: true,
            progressCurrent: Math.round(totalTimeMinutes), progressTotal: 6000,
            quip: totalTimeMinutes >= 6000
                ? "100 godzin. Absolutna chwilka przy matmie."
                : "100 godzin matematyki.",
        },
    ], [totalSolved, totalTimeMinutes]);

    const fieldMilestones = useMemo(() => {
        if (!allVisibleFields.length || !fetchedData) return [];
        return allVisibleFields.map(field => {
            const solved = fieldStatsMapping[field.field_id]?.total_problems_solved || 0;
            const ach = getFieldAchievement(field);
            return {
                id: `field_${field.field_id}_100`,
                label: ach.title,
                sublabel: field.field_name,
                icon: ach.icon,
                color: ach.color,
                bgColor: ach.bgColor,
                achieved: solved >= 67,
                progressCurrent: solved,
                progressTotal: 67,
                quip: solved >= 67 ? ach.quipAchieved : ach.quipPending,
            };
        });
    }, [allVisibleFields, fieldStatsMapping, fetchedData]);

    return (
        <Page showNav={true}>
        <div className="bg-white lg:bg-transparent w-full">
            <div className="flex flex-col max-w-2xl mx-auto w-full px-4 lg:px-0">
                <div className="w-full lg:px-10 py-6 lg:py-10 lg:mt-[8vh] lg:bg-white lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200">

                    {/* HEADER */}
                    <div className="mb-6 text-center">
                        <h1 className="text-2xl font-apotek tracking-wider text-[#233855] uppercase leading-none">
                            Osiągnięcia
                        </h1>
                    </div>

                    {/* STREAK HERO */}
                    {accountStatus && (() => {
                        const badge = getBadgeStatus(accountStatus.current_streak ?? 0);
                        return (
                            <StreakHeroCard
                                streak={accountStatus.current_streak ?? 0}
                                badgeImage={badge.image}
                                badgeTextColor={badge.textColor}
                                freezes={accountStatus.streak_freezes ?? 0}
                                totalSolved={totalSolved}
                                totalTimeMinutes={totalTimeMinutes}
                            />
                        );
                    })()}

                    {/* TWOJE RANGI */}
                    {allVisibleFields.length > 0 && (
                        <section className="mb-8">
                            <h2 className="font-apotek tracking-widest text-[13px] text-[#233855] uppercase mb-3">Twoje Rangi</h2>
                            <div className="grid grid-cols-2 gap-2.5">
                                {allVisibleFields.map(field => (
                                    <motion.div
                                        key={field.field_id}
                                        layout
                                        className={selectedRankFieldId === field.field_id ? 'col-span-2' : ''}
                                        transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                                    >
                                        <FieldRankCard
                                            field={field}
                                            stats={fieldStatsMapping[field.field_id] || {}}
                                            isSelected={selectedRankFieldId === field.field_id}
                                            onClick={() => setSelectedRankFieldId(prev => prev === field.field_id ? null : field.field_id)}
                                        />
                                    </motion.div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* KAMIENIE MILOWE */}
                    {!fetchedData && (
                        <div className="animate-pulse space-y-3 mb-8">
                            <div className="h-3 w-32 bg-gray-100 rounded" />
                            <div className="grid grid-cols-2 gap-2.5">
                                {[1,2,3,4].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl" />)}
                            </div>
                        </div>
                    )}
                    {fetchedData && (
                        <section className="mb-8">
                            <h2 className="font-apotek tracking-widest text-[13px] text-[#233855] uppercase mb-3">Kamienie Milowe</h2>
                            <div className="grid grid-cols-2 gap-2.5">
                                {milestones.map(m => (
                                    <motion.div
                                        key={m.id}
                                        layout
                                        className={selectedMilestoneId === m.id ? 'col-span-2' : ''}
                                        transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                                    >
                                        <AchievementTile
                                            label={m.label}
                                            sublabel={m.sublabel}
                                            icon={m.icon}
                                            achieved={m.achieved}
                                            color={m.color}
                                            bgColor={m.bgColor}
                                            isSelected={selectedMilestoneId === m.id}
                                            onClick={() => setSelectedMilestoneId(prev => prev === m.id ? null : m.id)}
                                            quip={m.quip}
                                            progressCurrent={m.progressCurrent}
                                            progressTotal={m.progressTotal}
                                            isTimed={m.isTimed || false}
                                            forceMinutes={m.forceMinutes || false}
                                        />
                                    </motion.div>
                                ))}
                                {fieldMilestones.map(m => (
                                    <motion.div
                                        key={m.id}
                                        layout
                                        className={selectedMilestoneId === m.id ? 'col-span-2' : ''}
                                        transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                                    >
                                        <AchievementTile
                                            label={m.label}
                                            sublabel={m.sublabel}
                                            icon={m.icon}
                                            achieved={m.achieved}
                                            color={m.color}
                                            bgColor={m.bgColor}
                                            isSelected={selectedMilestoneId === m.id}
                                            onClick={() => setSelectedMilestoneId(prev => prev === m.id ? null : m.id)}
                                            quip={m.quip}
                                            progressCurrent={m.progressCurrent}
                                            progressTotal={m.progressTotal}
                                        />
                                    </motion.div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* ANALITYKA */}
                    <section className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="font-apotek tracking-widest text-[13px] text-[#233855] uppercase">Analityka</h2>
                            <div className="relative flex items-center gap-4">
                                {showDateRange && (
                                    <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}
                                        className="text-xs font-apotek tracking-widest bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#233855]">
                                        <option value="week">Ostatni tydzień</option>
                                        <option value="twoWeeks">Ostatnie 2 tygodnie</option>
                                        <option value="month">Ostatni miesiąc</option>
                                        <option value="threeMonths">Ostatnie 3 miesiące</option>
                                        <option value="year">Ostatni rok</option>
                                    </select>
                                )}
                                <FaCog onClick={() => setIsPanelOpen(!isPanelOpen)} size={22} className="cursor-pointer text-[#233855] hover:text-[#345081] transition-colors" />
                                {isPanelOpen && (
                                    <div ref={panelRef} className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl ring-1 ring-gray-900/5 p-4 border border-gray-200 z-50">
                                        <h3 className="text-base font-apotek font-semibold text-[#233855] mb-4">Konfiguracja</h3>
                                        <div className="mb-4 max-h-60 overflow-y-auto pr-2">
                                            <h4 className="text-xs font-light mb-2">Wybierz dziedziny na wykresie:</h4>
                                            {Object.keys(groupedFields).sort().map((level) => {
                                                const visible = groupedFields[level].filter(f => f.is_visible);
                                                if (visible.length === 0) return null;
                                                return (
                                                    <div key={level} className="mb-2">
                                                        <h5 className="text-xs font-semibold text-gray-800 mb-1">{level}</h5>
                                                        <div className="pl-2 border-l-2 border-gray-200">
                                                            {visible.sort((a, b) => a.field_id - b.field_id).map(f => (
                                                                <div key={f.field_id} className="mb-1">
                                                                    <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                                                                        <input type="checkbox" checked={tempSelectedFields.includes(f.field_id)} onChange={() => handleFieldToggle(f.field_id)} className="mr-2 h-4 w-4 rounded border-gray-300 text-[#233855] focus:ring-[#345081]" />
                                                                        {f.field_name}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="mb-4">
                                            <h4 className="text-xs font-light mb-2">Wybierz metrykę:</h4>
                                            <select value={tempSelectedMetric} onChange={(e) => setTempSelectedMetric(e.target.value)} className="block w-full p-2 rounded-md bg-gray-50 border border-gray-300 text-sm focus:border-[#233855] focus:ring-[#233855]">
                                                <option value="elo">Punkty ELO</option>
                                                <option value="total_problems">{metricNames["total_problems"]}</option>
                                                <option value="total_problems_solved">{metricNames["total_problems_solved"]}</option>
                                                <option value="user_solving_time_spent_minutes">{metricNames["user_solving_time_spent_minutes"]}</option>
                                            </select>
                                        </div>
                                        <div className="mb-4">
                                            <h4 className="text-xs font-light mb-2">Wybierz typ wykresu:</h4>
                                            <select value={tempChartType} onChange={(e) => setTempChartType(e.target.value)} className="block w-full p-2 rounded-md bg-gray-50 border border-gray-300 text-sm focus:border-[#233855] focus:ring-[#233855]">
                                                <option value="radar">Radarowy</option>
                                                <option value="line">Liniowy</option>
                                            </select>
                                        </div>
                                        <button onClick={handleRefreshClick} className="w-full flex justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081]">
                                            Zapisz zmiany
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="h-[250px] sm:h-[300px] lg:h-[350px] w-full">
                            {isRadarChart && radarData
                                ? <Radar data={radarData} options={radarOptions} />
                                : lineData
                                    ? <Line data={lineData} options={lineOptions} />
                                    : <div className="w-full h-full rounded-xl bg-gray-100 animate-pulse" />
                            }
                        </div>
                    </section>

                    {/* POSTĘPY W DZIEDZINACH */}
                    {!fetchedData && (
                        <div className="animate-pulse space-y-3 mb-8">
                            <div className="h-3 w-44 bg-gray-100 rounded" />
                            {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-xl" />)}
                        </div>
                    )}
                    {fetchedData && (
                        <section className="mb-8">
                            <h2 className="font-apotek tracking-widest text-[13px] text-[#233855] uppercase mb-3">Postępy w Dziedzinach</h2>
                            {Object.keys(groupedFields).sort().map((level) => {
                                const visible = groupedFields[level].filter(f => f.is_visible);
                                if (visible.length === 0) return null;
                                return (
                                    <div key={level}>
                                        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider my-3">{level}</h4>
                                        <div className="space-y-2">
                                            {visible.sort((a, b) => a.field_id - b.field_id).map(f => (
                                                <FieldStatistics key={f.field_id} field={f} stats={fieldStatsMapping[f.field_id] || {}} onOpen={() => handleOpenStatsSheet(f, fieldStatsMapping[f.field_id] || {})} />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </section>
                    )}

                </div>
                <div className="h-[13vh] flex-shrink-0" />
            </div>
        </div>

        <AnimatePresence>
            {statsSheet && (
                <FieldStatsSheet
                    key="field-stats-sheet-root"
                    field={statsSheet.field}
                    stats={statsSheet.stats}
                    subfields={statsSubfieldsMap[statsSheet.field.field_id] || []}
                    isLoading={statsLoadingFieldId === statsSheet.field.field_id}
                    onClose={() => setStatsSheet(null)}
                />
            )}
        </AnimatePresence>
        </Page>
    );
};

export default UserStats;
